import { Project, ProjectStatus, ProjectTeamMember } from "../../models";
import { PROTO_PROJECTS } from "../prototype/projects-data";
import { DIVISIONS } from "../prototype/divisions-data";
import { PROTO_PERSONAS } from "../prototype/personas-data";

const DIVS = DIVISIONS as Record<string, any>;

/** name -> persona id, from the real directory (for resolving owner/superintendent). */
const PERSONA_BY_NAME: Record<string, string> = Object.fromEntries(
  Object.values(PROTO_PERSONAS as Record<string, any>).map((p) => [p.name, p.id]),
);

function teamFrom(p: any): ProjectTeamMember[] {
  const team: ProjectTeamMember[] = [];
  const at = p.start && p.start !== "TBD" ? p.start : null;
  const add = (name: string, positionTitle: string) => {
    if (!name || name === "TBD") return;
    team.push({ personaId: PERSONA_BY_NAME[name] ?? null, name, positionTitle, assignedAt: at });
  };
  add(p.owner, "Project Manager");
  add(p.superintendent, "Superintendent");
  // The CEO sponsors the two flagship jobs (mirrors the prototype's buildSeedProjects).
  if ((p.id === "proj-apex" || p.id === "proj-summit") && PERSONA_BY_NAME["Joseph Kingston"]) {
    add("Joseph Kingston", "Executive Sponsor");
  }
  return team;
}

/** Parse a money string like "$1.2M" / "$420K" into a number (mirrors the prototype parseMoney). */
function parseMoney(v: unknown): number {
  if (typeof v === "number") return v;
  if (!v) return 0;
  const s = String(v).replace(/[$,\s]/g, "");
  const m = s.match(/^([\d.]+)\s*([MmKk]?)/);
  if (!m) return 0;
  let n = parseFloat(m[1]) || 0;
  if (/[Mm]/.test(m[2])) n *= 1e6;
  else if (/[Kk]/.test(m[2])) n *= 1e3;
  return n;
}

/** Projects actually underway carry an actual start a few days after the scheduled one (mirrors the prototype). */
const ACTUAL_STARTS: Record<string, string> = {
  "proj-apex": "2025-09-18",
  "proj-summit": "2025-06-02",
  "proj-harbor": "2026-01-08",
  "proj-cedar": "2026-02-12",
  "proj-willow": "2026-03-02",
};

const KNOWN: ProjectStatus[] = ["backlog", "active", "on-track", "at-risk", "complete"];
const normStatus = (s: unknown): ProjectStatus =>
  KNOWN.includes(s as ProjectStatus) ? (s as ProjectStatus) : "active";

/**
 * Projects seed — mapped from the prototype PROTO_PROJECTS (the source of truth)
 * into the app model. Project identities and headline values (name, client,
 * owner, superintendent, phase, contract, SPI/CPI, schedule %, crew, status,
 * risks, location, dates) come straight from the prototype. The team roster is
 * derived like the prototype's buildSeedProjects (owner→PM, superintendent, plus
 * the CEO as Executive Sponsor on the flagship jobs); work sessions and time are
 * generated separately by withSampleSchedules; issues/checklists start empty.
 */
export const PROJECTS_SEED: readonly Project[] = (PROTO_PROJECTS as any[]).map((p): Project => {
  const osId = p.osId || DIVS[p.division]?.process || "CMOS";
  return {
    id: p.id,
    name: p.name,
    client: p.client,
    osId,
    division: p.division,
    status: normStatus(p.status),
    description: p.scope || "",
    address: { street: p.location || "", city: "", state: "", postalCode: "" },
    contractValue: parseMoney(p.contractValue),
    budgetCost: parseMoney(p.budgetCost),
    owner: p.owner || "TBD",
    superintendent: p.superintendent || "TBD",
    crew: Number(p.crew) || 0,
    spi: p.spi ?? null,
    cpi: p.cpi ?? null,
    schedulePct: Number(p.schedulePct) || 0,
    budgetPct: Number(p.budgetPct) || 0,
    start: p.start && p.start !== "TBD" ? p.start : null,
    end: p.subComplete && p.subComplete !== "TBD" ? p.subComplete : null,
    actualStart: p.actualStart || ACTUAL_STARTS[p.id] || null,
    currentPhaseNum: Number(p.phase) || 0,
    risks: Array.isArray(p.risks) ? p.risks : [],
    openShifts: Array.isArray(p.openShifts) ? p.openShifts : [],
    team: teamFrom(p),
    workSessions: [],
    timeEntries: [],
    issues: [],
    nextSteps: [],
    phaseLog: [],
    phaseChecklists: {},
    ...signoffRoles(p),
  };
});

/**
 * Seed the checklist sign-off roles: the PM (owner) prepares, the superintendent
 * reviews (falling back to the owner), and the principal (Joseph Kingston) gives
 * final review. Each is a list so more people can be added later.
 */
function signoffRoles(p: any): {
  preparerIds: string[];
  reviewerIds: string[];
  finalReviewerIds: string[];
} {
  const ownerId = PERSONA_BY_NAME[p.owner] ?? null;
  const superId = p.superintendent && p.superintendent !== 'TBD' ? PERSONA_BY_NAME[p.superintendent] ?? null : null;
  const ceoId = PERSONA_BY_NAME['Joseph Kingston'] ?? null;
  return {
    preparerIds: ownerId ? [ownerId] : [],
    reviewerIds: superId ? [superId] : ownerId ? [ownerId] : [],
    finalReviewerIds: ceoId ? [ceoId] : [],
  };
}
